/* ============================================================
   INFERMONDS — store behaviour on Shopify.

   This replaces public/js/store/store.js and cart.js. Everything those
   files did by hand — rendering products, holding a cart in localStorage,
   smuggling the chosen size to Stripe in client_reference_id — Shopify now
   does properly, so what is left is the interaction only.

   main.js is untouched and still owns the opening sequence. The two meet at
   one event, "infermonds:opened", exactly as before.
   ============================================================ */
(function () {
  "use strict";

  const $  = s => document.querySelector(s);
  const $$ = s => Array.from(document.querySelectorAll(s));
  const money = cents =>
    (cents / 100).toLocaleString(undefined, {
      style: "currency",
      currency: window.Shopify?.currency?.active || "GBP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

  /* ---------------- size picker ----------------
     A variant is not chosen until the customer chooses it. The button stays
     disabled until then rather than defaulting to the first size — a default
     size is how someone ends up with an M they did not ask for. */
  function wireSizes() {
    const sizes = $("#sizes");
    const field = $("#variantId");
    const add   = $("#addBtn");
    if (!sizes || !field || !add) return;

    sizes.addEventListener("click", e => {
      const b = e.target.closest(".size");
      if (!b || b.disabled) return;

      field.value = b.dataset.variant;
      for (const s of sizes.querySelectorAll(".size")) {
        s.setAttribute("aria-pressed", String(s === b));
      }
      add.disabled = false;
      add.classList.remove("btn--ghost");
      add.textContent = `${add.dataset.label} · ${b.dataset.size}`;
    });
  }

  /* ---------------- add to bag ----------------
     Posted over the AJAX cart API so the page does not reload. A reload here
     would restart the opening sequence from the top, and nobody wants to
     watch the stars again because they picked a size. */
  function wireAdd() {
    const form = $("#buyForm");
    if (!form) return;

    form.addEventListener("submit", async e => {
      e.preventDefault();
      const add = $("#addBtn");
      const id  = $("#variantId")?.value;
      if (!id) return;

      const was = add.textContent;
      add.disabled = true;
      add.textContent = "Adding…";

      try {
        const r = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
        });
        if (!r.ok) throw new Error((await r.json()).description || "Could not add");
        await refresh();
        openCart();
        add.textContent = was;
        add.disabled = false;
      } catch (err) {
        /* Say what went wrong where the finger already is, rather than in a
           console nobody has open. Sold out mid-session is the common case. */
        add.textContent = err.message.slice(0, 40) || "Unavailable";
        setTimeout(() => { add.textContent = was; add.disabled = false; }, 2600);
      }
    });
  }

  /* ---------------- drawer ---------------- */
  async function refresh() {
    const cart = await fetch("/cart.js").then(r => r.json());

    for (const el of $$("[data-cart-count]")) el.textContent = `Cart (${cart.item_count})`;

    const body = $("#cartBody");
    const foot = $("#cartFoot");
    if (!body || !foot) return;

    if (!cart.item_count) {
      body.innerHTML = `<p class="pdp__note">Your bag is empty.</p>`;
      foot.innerHTML = "";
      return;
    }

    body.innerHTML = cart.items.map(i => `
      <div class="line" data-key="${i.key}">
        <span class="line__name">${esc(i.product_title)}</span>
        ${/Default/.test(i.variant_title || "") ? "" :
          `<span class="line__size">${esc(i.variant_title)}</span>`}
        <span class="line__price">${money(i.final_line_price)}</span>
        <button type="button" class="line__remove" data-remove="${i.key}"
                aria-label="Remove ${esc(i.product_title)}">Remove</button>
      </div>`).join("");

    foot.innerHTML = `
      <p class="pdp__price">${money(cart.total_price)}</p>
      <a class="btn btn--ghost" href="/checkout">Checkout</a>
      <p class="pdp__note">Shipping and taxes calculated at checkout.</p>`;
  }

  const esc = s => String(s).replace(/[&<>"']/g, c =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

  function openCart()  { document.body.classList.add("cart-open"); }
  function closeCart() { document.body.classList.remove("cart-open"); }

  function wireCart() {
    $("#cartBtn")?.addEventListener("click", async () => { await refresh(); openCart(); });
    $("#cartClose")?.addEventListener("click", closeCart);
    $("#scrim")?.addEventListener("click", () => { closeCart(); closeMenu(); });

    document.addEventListener("click", async e => {
      const key = e.target.closest("[data-remove]")?.dataset.remove;
      if (!key) return;
      await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: key, quantity: 0 }),
      });
      await refresh();
    });

    document.addEventListener("keydown", e => {
      if (e.key === "Escape") { closeCart(); closeMenu(); }
    });
  }

  /* ---------------- menu ---------------- */
  function closeMenu() {
    document.body.classList.remove("menu-open");
    $("#burger")?.setAttribute("aria-expanded", "false");
  }
  function wireMenu() {
    $("#burger")?.addEventListener("click", () => {
      const open = document.body.classList.toggle("menu-open");
      $("#burger").setAttribute("aria-expanded", String(open));
    });
  }

  /* ---------------- the mark in the header ----------------
     store.js used to fill every [data-brand-mark] from the logo path. The
     intro depends on it: at the handover the canvas mark fades out while
     this SVG fades in over the same pixels, so an empty element here means
     the mark disappears at the exact moment the sequence lands it in the
     header — the one frame the whole opening is built around.

     Filled immediately rather than on "infermonds:opened", because main.js
     measures this element's box to know where to walk the mark to. */
  const markSvg = () =>
    `<svg viewBox="0 0 100 100" aria-hidden="true"><path d="${
      window.INFERMONDS_LOGO.path}" fill="currentColor" fill-rule="evenodd"/></svg>`;

  if (window.INFERMONDS_LOGO) {
    for (const el of $$("[data-brand-mark]")) el.innerHTML = markSvg();
  }

  /* ---------------- revealing the store ----------------
     .shell is visibility:hidden until body.store-open, and that same class
     drives the [data-rise] entrance in store.css — the staggered delays
     that bring the wordmark, nav and brand mark up in sequence. The brand
     mark's 0.95s is timed to meet the canvas mark as it lands, so this is
     choreography, not a show/hide.

     store.js used to own this. Without it the whole page renders, measures
     4000px tall, and stays invisible. */
  const reduceMotion =
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const show = () => document.body.classList.add("store-open");

  window.addEventListener("infermonds:opened", show, { once: true });

  /* Safety net, carried over verbatim in spirit: if the intro ever throws,
     the store still has to appear. Polled rather than set on one timeout,
     and only while the tab is visible — a background tab has a frozen rAF
     clock, and tripping the net there would rob a first-time visitor of the
     opening entirely. */
  if (reduceMotion) {
    setTimeout(show, 300);
  } else {
    const startedAt = performance.now();
    const net = setInterval(() => {
      if (document.body.classList.contains("store-open")) return clearInterval(net);
      if (document.visibilityState !== "visible") return;
      if (performance.now() - startedAt < 11000) return;
      console.warn("[store] intro did not hand over — opening anyway");
      show();
      clearInterval(net);
    }, 500);
  }

  /* ---------------- start ----------------
     Nothing is wired until the opening has handed over, so a click cannot
     land on a control that is still behind the intro. On every page after
     the first, main.js fires this immediately from sessionStorage. */
  function start() { wireSizes(); wireAdd(); wireCart(); wireMenu(); refresh(); }

  if (document.body.classList.contains("instant-open")) start();
  else window.addEventListener("infermonds:opened", start, { once: true });
})();
